// TableRowDetailsModal.jsx
import React, { useEffect, useState, useCallback } from "react";
import "./TableRowDetailsModal.css";
import { getValue } from "./utils";
import TableRowPdfButton from "./TableRowPdfButton";
import DynamicButton from "../DynamicButton/DynamicButton";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";

// -------- helpers ----------
function isImageUrl(url = "") {
  const u = String(url || "").split("?")[0];
  return /\.(png|jpe?g|webp|gif|svg)$/i.test(u);
}
function isHttpUrl(s = "") {
  try {
    const u = new URL(String(s));
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

// Parse "(lat, lng)" or "lat,lng" -> {lat, lng} | null
function parseLatLng(geo = "") {
  const m = String(geo || "").match(/(-?\d+(\.\d+)?)[,\s)]+(-?\d+(\.\d+)?)/);
  if (!m) return null;
  return { lat: m[1], lng: m[3] };
}
function googleMapsHref(geo = "") {
  const p = parseLatLng(geo);
  if (!p) return null;
  return `https://www.google.com/maps?q=${encodeURIComponent(
    p.lat
  )},${encodeURIComponent(p.lng)}&z=15`;
}
function googleMapsEmbedSrc(geo = "") {
  const p = parseLatLng(geo);
  if (!p) return null;
  return `https://www.google.com/maps?q=${encodeURIComponent(
    p.lat
  )},${encodeURIComponent(p.lng)}&z=15&output=embed`;
}

function isEmpty(val) {
  if (val === null || val === undefined) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === "object" && !Array.isArray(val)) {
    return Object.keys(val).length === 0;
  }
  return false;
}
function prettyJSON(val) {
  try {
    return JSON.stringify(val, null, 2);
  } catch {
    return String(val);
  }
}

// Try common label keys when rendering array/object items
function objectLabel(obj, col) {
  if (!obj || typeof obj !== "object") return "";
  const keyPrefs = [
    col?.apiTitle,
    "label",
    "name",
    "hazards",
    "tools",
    "machineTools",
    "equipments",
    "permitsType",
    "projectName",
    "siteLocation",
    "value",
    "title",
  ].filter(Boolean);
  for (const k of keyPrefs) {
    if (k in obj && obj[k] !== undefined && obj[k] !== null) {
      return String(obj[k]);
    }
  }
  return JSON.stringify(obj);
}

function getFileName(url = "") {
  try {
    const clean = decodeURI(String(url));
    return clean.split("?")[0].split("#")[0].split("/").pop() || "file";
  } catch {
    return String(url).split("?")[0].split("#")[0].split("/").pop() || "file";
  }
}
function fileExt(url = "") {
  const n = getFileName(url);
  const m = n.match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : "";
}
function isPreviewableImage(url = "") {
  return /\.(png|jpe?g|webp|gif|svg)$/i.test(String(url).split("?")[0]);
}

// For dropdowns: emulate your form chip logic (baseField + apiTitle)
function dropdownLabelFromRow(row, col) {
  const { field, options, apiTitle } = col;
  const baseField = String(field || "").split(".")[0]; // e.g., 'project' from 'project.projectName'
  const selected = row?.[baseField];

  // If nested object is present, use apiTitle (or common fallbacks)
  if (selected && typeof selected === "object") {
    return (
      (apiTitle && selected[apiTitle]) ||
      selected.name ||
      selected.label ||
      selected.title ||
      selected.value ||
      // try the last part of field when object exists (e.g. projectName)
      selected[field.split(".").slice(1).join(".")] ||
      ""
    );
  }

  // If primitive + static options
  if (options && Array.isArray(options)) {
    if (options.includes(selected)) return selected;
  }

  // Fallback: try full field path value (often already a label string)
  const full = getValue(row, field);
  if (typeof full === "string" && full.trim()) return full;

  return selected || ""; // id/string fallback
}

// -------- component ----------
export default function TableRowDetailsModal({
  open,
  row,
  columns = [],
  onClose,
  onEdit, // () => void
  onDelete, // () => void
  title = "Details",

  onPrev, // () => void
  onNext, // () => void
  hasPrev = true, // boolean (disable left arrow)
  hasNext = true, // boolean (disable right arrow)
  loop = false, // optional: if true, allow prev/next even when flags false
}) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRequestDelete = () => setShowConfirm(true);
  const handleConfirmDelete = () => {
    setShowConfirm(false);
    onDelete?.(); // fire delete passed from parent
  };
  const handleCancelDelete = () => setShowConfirm(false);

    const handleKeyDown = useCallback(
      (e) => {
        if (!open) return;
        if (e.key === "Escape") {
          onClose?.();
        } else if (e.key === "ArrowLeft") {
          if (loop || hasPrev) onPrev?.();
        } else if (e.key === "ArrowRight") {
          if (loop || hasNext) onNext?.();
        }
      },
      [open, onClose, onPrev, onNext, hasPrev, hasNext, loop]
    );

      useEffect(() => {
        if (!open) return;
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
      }, [open, handleKeyDown]);

  if (!open || !row) return null;

  return (
    <div className="row-modal-overlay" role="dialog" aria-modal="true">
      <div className="row-modal">
        {/* Header */}
        <div className="row-modal-header">
          <h3 className="row-modal-title">{title}</h3>
          <button
            className="row-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="row-modal-content">
          <div className="row-modal-grid">
            {columns.map((col, i) => {
              const label = col.label || col.field;
              const raw = getValue(row, col.field);

              // Always show a dash for empty-ish values
              if (isEmpty(raw)) {
                // Special-cases still show a dash
                return (
                  <div key={i} className="row-field">
                    <div className="row-field-label">{label}</div>
                    <div className="row-field-value">
                      <span className="row-dash">—</span>
                    </div>
                  </div>
                );
              }

              if (col.type === "imageLink" && typeof raw === "string") {
                if (!isHttpUrl(raw)) {
                  return (
                    <div key={i} className="row-field">
                      <div className="row-field-label">{label}</div>
                      <div className="row-field-value">
                        <span className="row-dash">—</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={i} className="row-field">
                    <div className="row-field-label">{label}</div>
                    <div className="row-field-value">
                      {isImageUrl(raw) ? (
                        <img
                          className="row-image"
                          src={raw}
                          alt={label}
                          onError={(e) => {
                            e.currentTarget.replaceWith(
                              Object.assign(document.createElement("span"), {
                                className: "row-dash",
                                textContent: "—",
                              })
                            );
                          }}
                          onClick={() =>
                            window.open(raw, "_blank", "noopener,noreferrer")
                          }
                        />
                      ) : (
                        <a
                          href={raw}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="row-link"
                        >
                          Open Link
                        </a>
                      )}
                    </div>
                  </div>
                );
              }

              // SIGNATURE field -> show image if exists
              if (col.type === "signature" && typeof raw === "string") {
                if (!isHttpUrl(raw)) {
                  return (
                    <div key={i} className="row-field">
                      <div className="row-field-label">{label}</div>
                      <div className="row-field-value">
                        <span className="row-dash">—</span>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={i} className="row-field">
                    <div className="row-field-label">{label}</div>
                    <div className="row-field-value">
                      <div className="signature-preview-container">
                        <img
                          className="row-signature"
                          src={raw}
                          alt={`${label} signature`}
                          onClick={() =>
                            window.open(raw, "_blank", "noopener,noreferrer")
                          }
                          onError={(e) => {
                            e.currentTarget.replaceWith(
                              Object.assign(document.createElement("span"), {
                                className: "row-dash",
                                textContent: "—",
                              })
                            );
                          }}
                        />
                        <div style={{ marginTop: 6 }}>
                          <a
                            href={raw}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="row-link"
                          >
                            View Full Signature
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // helper: safely get nested value using dot path
              function getNestedValue(obj, path) {
                return path.split(".").reduce((acc, key) => {
                  if (acc && typeof acc === "object" && key in acc) {
                    return acc[key];
                  }
                  return undefined;
                }, obj);
              }

              if (col.type === "dropdown") {
                const labelText = dropdownLabelFromRow(row, col);

                // resolve the base object (before any dot path like .name)
                const baseField = col.field.includes(".")
                  ? col.field.split(".")[0] // e.g. "observer.name" -> "observer"
                  : col.field;

                const selectedOption = row[baseField]; // full object

                return (
                  <div key={i} className="row-field">
                    <div className="row-field-label">{label}</div>
                    <div className="row-field-value">
                      {labelText ? (
                        <span className="row-chip">{labelText}</span>
                      ) : (
                        <span className="row-dash">—</span>
                      )}
                    </div>

                    {/* 🔹 Details Box inside popup */}
                    {col.details === "True" && selectedOption && (
                      <div className="mt-3 w-full rounded-lg border border-gray-200 bg-white shadow-sm">
                        <ul className="divide-y divide-gray-200">
                          {col.BoxShow?.map((fieldKey) => {
                            const value = getNestedValue(
                              selectedOption,
                              fieldKey
                            );

                            let displayValue;
                            if (value == null) {
                              displayValue = "—";
                            } else if (typeof value === "object") {
                              displayValue = JSON.stringify(value); // fallback
                            } else {
                              displayValue = value;
                            }

                            return (
                              <li
                                key={fieldKey}
                                className="px-4 py-3 text-sm sm:flex sm:items-center sm:justify-between"
                              >
                                <div className="font-medium text-gray-600 sm:w-1/3">
                                  {fieldKey}
                                </div>
                                <div className="mt-1 text-gray-900 sm:mt-0 sm:w-2/3 break-words">
                                  {displayValue}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              }

              // ARRAY-LIKE (checkbox arrays etc.) -> chips
              // ARRAY-LIKE (checkbox arrays etc.) -> chips
              if (
                (col.type === "checkbox" && col.array === "true") ||
                Array.isArray(raw)
              ) {
                let items = Array.isArray(raw) ? raw : [];

                // If items are empty but the field is a dot-path and base is an array
                if (
                  !items.length &&
                  typeof col.field === "string" &&
                  col.field.includes(".")
                ) {
                  const [base /* e.g. "hazards" */, ...rest] =
                    col.field.split(".");
                  const baseVal = row?.[base];
                  if (Array.isArray(baseVal)) {
                    // we’ll render the objects themselves; objectLabel will pick col.apiTitle (e.g. "hazards")
                    items = baseVal;
                  }
                }

                return (
                  <div key={i} className="row-field">
                    <div className="row-field-label">{label}</div>
                    <div className="row-field-value">
                      {items.length ? (
                        <div className="row-chipset">
                          {items.map((x, idx) => (
                            <span key={idx} className="row-chip">
                              {typeof x === "object"
                                ? objectLabel(x, col)
                                : String(x)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="row-dash">—</span>
                      )}
                    </div>
                  </div>
                );
              }

              // GEOLOCATION -> embed map here + link
              if (col.type === "geolocation") {
                const embedSrc = googleMapsEmbedSrc(raw);
                const href = googleMapsHref(raw);
                return (
                  <div key={i} className="row-field row-field-map">
                    <div className="row-field-label">{label}</div>
                    <div className="row-field-value">
                      {embedSrc ? (
                        <>
                          <div
                            className="row-map-embed"
                            style={{
                              width: "100%",
                              height: 240,
                              borderRadius: 10,
                              overflow: "hidden",
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            <iframe
                              title="Map"
                              src={embedSrc}
                              width="100%"
                              height="100%"
                              style={{ border: 0 }}
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                            />
                          </div>
                          {href && (
                            <div style={{ marginTop: 8 }}>
                              <a
                                className="row-link"
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Open in Google Maps
                              </a>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="row-dash">—</span>
                      )}
                    </div>
                  </div>
                );
              }

              // BOOLEANS -> Yes/No
              if (typeof raw === "boolean") {
                return (
                  <div key={i} className="row-field">
                    <div className="row-field-label">{label}</div>
                    <div className="row-field-value">{raw ? "Yes" : "No"}</div>
                  </div>
                );
              }

              // --- PERMITCHECKLISTS ---
              if (col.type === "permitchecklists") {
                let items = [];
                const rawVal = getValue(row, col.field);

                if (Array.isArray(rawVal)) {
                  items = rawVal;
                } else if (
                  typeof col.field === "string" &&
                  col.field.includes(".")
                ) {
                  const [base] = col.field.split(".");
                  if (Array.isArray(row?.[base])) items = row[base];
                } else if (Array.isArray(row?.SafetyChecks)) {
                  items = row.SafetyChecks;
                }

                const checkLabel =
                  col.subFields?.find((s) => s.field === "CheckPoints")
                    ?.label || "Check Points";
                const respLabel =
                  col.subFields?.find((s) => s.field === "response")?.label ||
                  "Response";

                return (
                  <div key={i} className="row-field">
                    <div className="row-field-label">
                      {col.label || col.field}
                    </div>
                    <div className="row-field-value">
                      {items.length ? (
                        <div style={{ overflowX: "auto" }}>
                          <table className="row-subtable">
                            <thead>
                              <tr>
                                <th>{checkLabel}</th>
                                <th style={{ textAlign: "center" }}>
                                  {respLabel}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((it, idx2) => (
                                <tr key={it._id ?? idx2}>
                                  <td>{it?.CheckPoints || "—"}</td>
                                  <td style={{ textAlign: "center" }}>
                                    {it?.response ?? "N/A"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <span className="row-dash">—</span>
                      )}
                    </div>
                  </div>
                );
              }

              // --- SAFETYCHECKS ---
              if (col.type === "safetychecks") {
                let items = [];
                const rawVal = getValue(row, col.field);

                if (Array.isArray(rawVal)) {
                  items = rawVal;
                } else if (
                  typeof col.field === "string" &&
                  col.field.includes(".")
                ) {
                  const [base] = col.field.split(".");
                  if (Array.isArray(row?.[base])) items = row[base];
                } else if (Array.isArray(row?.SafetyChecks)) {
                  items = row.SafetyChecks;
                }

                return (
                  <div key={i} className="row-field row-field-subtable">
                    <div className="row-field-label">
                      {col.label || col.field}
                    </div>
                    <div className="row-field-value">
                      {items.length ? (
                        <div style={{ overflowX: "auto" }}>
                          <table className="row-subtable">
                            <thead>
                              <tr>
                                {col.subFields.map((sf) => (
                                  <th key={sf.field}>{sf.label || sf.field}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((it, idx2) => (
                                <tr key={it._id ?? idx2}>
                                  {col.subFields.map((sf) => {
                                    const val = getValue(it, sf.field);

                                    if (
                                      sf.type === "imageLink" &&
                                      typeof val === "string"
                                    ) {
                                      return (
                                        <td key={sf.field}>
                                          {val ? (
                                            <img
                                              src={val}
                                              alt={sf.label}
                                              style={{
                                                maxHeight: 60,
                                                borderRadius: 6,
                                              }}
                                              onClick={() =>
                                                window.open(
                                                  val,
                                                  "_blank",
                                                  "noopener,noreferrer"
                                                )
                                              }
                                              onError={(e) =>
                                                (e.currentTarget.style.display =
                                                  "none")
                                              }
                                            />
                                          ) : (
                                            "—"
                                          )}
                                        </td>
                                      );
                                    }

                                    if (sf.type === "dropdown") {
                                      return (
                                        <td
                                          key={sf.field}
                                          style={{ textAlign: "center" }}
                                        >
                                          {val ? (
                                            <span className="row-chip">
                                              {String(val)}
                                            </span>
                                          ) : (
                                            sf.prefilled || "—"
                                          )}
                                        </td>
                                      );
                                    }

                                    if (val && typeof val === "object") {
                                      return (
                                        <td key={sf.field}>
                                          {objectLabel(val, sf) || "—"}
                                        </td>
                                      );
                                    }

                                    return (
                                      <td key={sf.field}>
                                        {val ? String(val) : "—"}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <span className="row-dash">—</span>
                      )}
                    </div>
                  </div>
                );
              }

              // --- SUBSTRING ---
              if (col.type === "subString") {
                let items = [];
                const rawVal = getValue(row, col.field);

                if (Array.isArray(rawVal)) {
                  items = rawVal;
                } else if (
                  typeof col.field === "string" &&
                  col.field.includes(".")
                ) {
                  const [base] = col.field.split(".");
                  if (Array.isArray(row?.[base])) items = row[base];
                }

                return (
                  <div key={i} className="row-field row-field-subtable">
                    <div className="row-field-label">
                      {col.label || col.field}
                    </div>
                    <div className="row-field-value">
                      {items.length ? (
                        <div style={{ overflowX: "auto" }}>
                          <table className="row-subtable">
                            <thead>
                              <tr>
                                {col.subFields.map((sf) => (
                                  <th key={sf.field}>{sf.label || sf.field}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((it, idx2) => (
                                <tr key={it._id ?? idx2}>
                                  {col.subFields.map((sf) => {
                                    const val = getValue(it, sf.field);

                                    if (
                                      sf.type === "imageLink" &&
                                      typeof val === "string"
                                    ) {
                                      return (
                                        <td key={sf.field}>
                                          {val ? (
                                            <img
                                              src={val}
                                              alt={sf.label}
                                              style={{
                                                maxHeight: 60,
                                                borderRadius: 6,
                                              }}
                                              onClick={() =>
                                                window.open(
                                                  val,
                                                  "_blank",
                                                  "noopener,noreferrer"
                                                )
                                              }
                                              onError={(e) =>
                                                (e.currentTarget.style.display =
                                                  "none")
                                              }
                                            />
                                          ) : (
                                            "—"
                                          )}
                                        </td>
                                      );
                                    }

                                    if (sf.type === "dropdown") {
                                      return (
                                        <td
                                          key={sf.field}
                                          style={{ textAlign: "center" }}
                                        >
                                          {val ? (
                                            <span className="row-chip">
                                              {String(val)}
                                            </span>
                                          ) : (
                                            sf.prefilled || "—"
                                          )}
                                        </td>
                                      );
                                    }

                                    if (val && typeof val === "object") {
                                      return (
                                        <td key={sf.field}>
                                          {objectLabel(val, sf) || "—"}
                                        </td>
                                      );
                                    }

                                    return (
                                      <td key={sf.field}>
                                        {val ? String(val) : "—"}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <span className="row-dash">—</span>
                      )}
                    </div>
                  </div>
                );
              }

              // FILE (single or array) -> thumbnails for images, links for others
              if (col.type === "file") {
                const baseField = String(col.field || "").split(".")[0];
                const rawVal = row?.[baseField];
                const files = Array.isArray(rawVal)
                  ? rawVal
                  : rawVal
                  ? [rawVal]
                  : [];

                return (
                  <div key={i} className="row-field">
                    <div className="row-field-label">{label}</div>
                    <div className="row-field-value">
                      {files.length ? (
                        <div className="row-chipset" style={{ gap: 10 }}>
                          {files.map((url, idx2) => {
                            if (!isHttpUrl(url)) {
                              return (
                                <span key={idx2} className="row-dash">
                                  —
                                </span>
                              );
                            }

                            const name = getFileName(url);
                            const ext = fileExt(url);

                            // Show image thumbnail if it's an image
                            if (isPreviewableImage(url)) {
                              return (
                                <img
                                  key={idx2}
                                  className="row-image"
                                  src={url}
                                  alt={name}
                                  title={name}
                                  onClick={() =>
                                    window.open(
                                      url,
                                      "_blank",
                                      "noopener,noreferrer"
                                    )
                                  }
                                  onError={(e) => {
                                    e.currentTarget.replaceWith(
                                      Object.assign(
                                        document.createElement("a"),
                                        {
                                          href: url,
                                          target: "_blank",
                                          rel: "noopener noreferrer",
                                          className: "row-link",
                                          textContent: name,
                                        }
                                      )
                                    );
                                  }}
                                />
                              );
                            }

                            // Non-image → link with filename (opens in new tab)
                            return (
                              <a
                                key={idx2}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="row-link"
                                title={name}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                {/* simple ext badge */}
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "2px 6px",
                                    borderRadius: 6,
                                    border:
                                      "1px solid var(--rm-border, #e5e7eb)",
                                    fontSize: 12,
                                    textTransform: "uppercase",
                                    color: "#475569",
                                    background: "var(--rm-surface, #f8fafc)",
                                  }}
                                >
                                  {ext || "file"}
                                </span>
                                <span>{name}</span>
                              </a>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="row-dash">—</span>
                      )}
                    </div>
                  </div>
                );
              }

              // STRINGS/NUMBERS/etc. (default)
              return (
                <div key={i} className="row-field">
                  <div className="row-field-label">{label}</div>
                  <div className="row-field-value">
                    {String(raw).trim() === "" ? (
                      <span className="row-dash">—</span>
                    ) : (
                      String(raw)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="row-modal-footer">
          <DynamicButton
            text="Delete"
            icon="fa-trash"
            size="md"
            variant="danger"
            onClick={handleRequestDelete}
          />

          <div className="row-spacer" />

          {/* NEW: Download PDF (uses same row + columns) */}
          <TableRowPdfButton
            row={row}
            columns={columns}
            title={title}
            filename={`${title.replace(/\s+/g, "_").toLowerCase()}_${
              row?._id || "record"
            }.pdf`}
            className="row-btn" // matches your existing button style
          />

          <DynamicButton
            text="Edit"
            icon="fa-edit"
            size="md"
            variant="outline"
            onClick={onEdit}
          />

          <div className="row-nav-group">
            <DynamicButton
              icon="fa-arrow-left"
              size="sm"
              variant="outline"
              onClick={() => (loop || hasPrev) && onPrev?.()}
              disabled={!loop && !hasPrev}
            />
            <DynamicButton
              icon="fa-arrow-right"
              size="sm"
              variant="outline"
              onClick={() => (loop || hasNext) && onNext?.()}
              disabled={!loop && !hasNext}
            />
          </div>

          <ConfirmDialog
            open={showConfirm}
            title="Delete this record?"
            message="Are you sure you want to delete this record? This action cannot be undone."
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            variant="danger"
          />
        </div>
      </div>
    </div>
  );
}
